import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';
import { 
  ArrowRight, 
  Cloud, 
  Moon, 
  Focus, 
  Flower2, 
  BatteryLow, 
  HelpCircle, 
  Users, 
  Leaf, 
  Heart, 
  HandHeart, 
  MessageCircle, 
  Pill, 
  Sparkles,
  Activity,
  Brain
} from 'lucide-react';

export default function Depression() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/depression_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching depression_side CMS data:', err);
      }
    };
    fetchCmsData();
  }, []);

  const seoTitle = lang === 'da' 
    ? 'Depression – Information og Behandling | Nordisk Psykiatri' 
    : 'Depression – Information and Treatment | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Depression er mere end almindelig tristhed. Få viden om symptomer, hvad der sker i hjernen, og hvordan behandling hos Nordisk Psykiatri kan hjælpe dig videre.'
    : 'Depression is more than ordinary sadness. Learn about symptoms, neural mechanisms in the brain, and how treatment at Nordisk Psykiatri can help.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [brainTab, setBrainTab] = useState('both'); // 'under', 'bedring', or 'both'

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqItems = lang === 'da' ? [
    {
      q: 'Hvad er forskellen på almindelig tristhed og depression?',
      a: 'Almindelig tristhed er en naturlig reaktion på en svær situation og aftager typisk med tiden. Ved en depression vedvarer nedtryktheden eller tabet af lyst i ugevis, påvirker hverdagen markant og ledsages ofte af forandringer i søvn, appetit, energi og selvfølelse.'
    },
    {
      q: 'Hvordan foregår et behandlingsforløb for depression?',
      a: 'Behandlingen tilpasses altid den enkelte. Den kan bestå af psykoterapi, samtaleterapi, rådgivning om hverdagsstruktur samt ved behov medicinsk behandling. Første skridt er en grundig samtale om din situation og dine behov.'
    },
    {
      q: 'Er medicin altid nødvendigt ved depression?',
      a: 'Nej. Ved milde til moderate depressioner kan psykoterapi og støttende samtaler ofte stå alene. Ved moderat til svær depression kan en kombination af psykoterapi og medicin give det bedste resultat.'
    },
    {
      q: 'Kan man blive helt fri for depression igen?',
      a: 'Ja, de fleste oplever markant bedring eller fuld helbredelse med den rette behandling og støtte. Tidlig indsats øger muligheden for et godt og stabilt genopretningsforløb.'
    }
  ] : [
    {
      q: 'What is the difference between ordinary sadness and clinical depression?',
      a: 'Ordinary sadness is a natural reaction to difficult circumstances and typically subsides over time. In depression, low mood or loss of interest persists for weeks, significantly impairs daily life, and is often accompanied by changes in sleep, appetite, energy, and self-worth.'
    },
    {
      q: 'How does treatment for depression take place?',
      a: 'Treatment is always tailored to the individual. It can include psychotherapy, structured counselling, lifestyle adjustments, and when indicated, psychiatric medical treatment.'
    },
    {
      q: 'Is medication always necessary for depression?',
      a: 'No. For mild to moderate depression, psychological therapy is often effective on its own. For moderate to severe depression, a combination of psychotherapy and medication typically yields the best outcomes.'
    },
    {
      q: 'Can one fully recover from depression?',
      a: 'Yes, most individuals experience substantial improvement or full recovery with appropriate treatment and support.'
    }
  ];

  return (
    <div className="bg-sand-light min-h-screen text-text-dark font-sans selection:bg-petrol-dark/10 selection:text-petrol-dark">
      
      {/* ==========================================
          SEKTION 1 – HERO SECTION
          ========================================== */}
      <FluidSubpageHero
        eyebrow={cmsData?.hero_badge ? getLocalizedValue(cmsData.hero_badge) : (lang === 'da' ? 'DEPRESSION – INFORMATION OG BEHANDLING' : 'DEPRESSION – INFORMATION AND TREATMENT')}
        title={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : (lang === 'da' ? 'Hvad er en\ndepression?' : 'What is\ndepression?')}
        imageUrl={cmsData?.hero_billede?.[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=2400,quality=95,format=auto/${cmsData.hero_billede[0].replace('https://assets.auxodev.dk/', '')}` : "https://assets.auxodev.dk/cdn-cgi/image/width=2400,quality=95,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-aug-28-2026-09-12-14-am.png"}
        imageAlt={lang === 'da' ? 'Upløftende og rolig skandinavisk atmosfære med blødt lys' : 'Calm, uplifting Scandinavian atmosphere with soft light'}
        organicShape={false}
        noBox={true}
        imageFit="contain"
        noHover={true}
        hasMask={true}
        description={cmsData?.hero_beskrivelse ? getLocalizedValue(cmsData.hero_beskrivelse) : (lang === 'da' 
          ? '<p>Depression er mere end almindelig tristhed. Det er en tilstand, hvor nedtrykthed eller tab af lyst vedvarer og påvirker hverdagen markant.</p><p>Det kan gøre det svært at passe arbejde, studier, relationer og daglige opgaver – og det kan påvirke energi, tanker, søvn og appetit.</p><p>For nogle kan nedtryktheden blive et beskyttende lag over vrede, sorg eller behov, som har været svære at give plads. Det er én mulig forståelse – ikke den samme forklaring for alle.</p>'
          : '<p>Depression is more than ordinary sadness. It is a mental health condition where low mood or loss of interest persists and significantly impacts daily life.</p><p>It can make it hard to manage work, studies, relationships, and routine tasks—and can profoundly affect energy, thoughts, sleep, and appetite.</p><p>For some, low mood can serve as a protective layer over anger, grief, or needs that have been difficult to express. That is one clinical perspective—not the identical explanation for everyone.</p>')}
        ctaText={cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : (lang === 'da' ? 'Book en indledende samtale' : 'Book an initial consultation')}
        ctaLink={cmsData?.hero_cta_link || cmsData?.cta_knap_link || "/kontakt"}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        reviewStamp={cmsData?.fagligt_gennemgaaet ? getLocalizedValue(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Martin Bergholtz, læge i psykiatri | Opdateret september 2026' : 'Clinically reviewed by Martin Bergholtz, Medical Doctor in Psychiatry | Updated September 2026')}
      />

      {/* ==========================================
          SEKTION 2 – TYPISKE SYMPTOMER
          ========================================== */}
      <section 
        className="relative w-full mt-8 sm:mt-12 lg:mt-16 py-[55px] pb-[65px] md:py-[75px] md:pb-[90px] px-6 sm:px-8 border-y border-sand-medium/40 text-[#173c38]" 
        id="symptomer"
        style={{
          background: 'radial-gradient(circle at 15% 35%, rgba(227, 239, 208, 0.75), transparent 38%), radial-gradient(circle at 85% 55%, rgba(217, 234, 194, 0.65), transparent 42%), #d1e1be'
        }}
      >
        <div className="max-w-[1140px] mx-auto">
          
          <div className="text-center mb-[35px] md:mb-[55px]">
            <h2 className="m-0 font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight leading-[1.15]">
              {cmsData?.symptomer_overskrift ? getLocalizedValue(cmsData.symptomer_overskrift) : (lang === 'da' ? 'Typiske symptomer' : 'Typical Symptoms')}
            </h2>
            <span className="block w-10 h-[2px] mx-auto mt-4 bg-[#8f843d]" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-x-12 lg:gap-x-16 relative">
            {/* Midterste lodrette skillelinje på desktop */}
            <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-[1px] bg-[#49604c]/20 -translate-x-1/2 pointer-events-none" />

            {/* Venstre kolonne (01 - 03) */}
            <div className="flex flex-col md:pr-8 lg:pr-12">
              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 md:first:pt-2 border-b border-[#465d49]/20 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">01</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Nedtrykthed' : 'Persistent Low Mood'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Vedvarende tristhed, tomhed eller modløshed.' 
                      : 'Persistent sadness, emptiness, or feelings of despair.'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 border-b border-[#465d49]/20 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">02</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Mindre lyst og interesse' : 'Loss of Interest & Pleasure'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Tab af glæde ved aktiviteter, du tidligere har holdt af.' 
                      : 'Loss of joy in activities and interests you previously appreciated.'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 border-b border-[#465d49]/20 md:border-b-0 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">03</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Søvn- og appetitændringer' : 'Sleep & Appetite Changes'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Forstyrret søvn, ændret appetit eller vægt.' 
                      : 'Disrupted sleep patterns, altered appetite, or weight changes.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Højre kolonne (04 - 06) */}
            <div className="flex flex-col md:pl-8 lg:pl-12">
              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 md:first:pt-2 border-b border-[#465d49]/20 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">04</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Lav energi' : 'Low Energy & Fatigue'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Følelse af udmattelse og at alting kræver mere energi end før.' 
                      : 'Sensation of exhaustion and everyday tasks requiring significantly more effort.'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 border-b border-[#465d49]/20 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">05</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Koncentrationsbesvær' : 'Difficulty Concentrating'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Vanskeligheder ved at holde fokus, huske og træffe beslutninger.' 
                      : 'Challenges with maintaining focus, remembering, and making everyday decisions.'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[40px_1fr] md:grid-cols-[48px_1fr] gap-4 md:gap-5 py-6 md:py-7 border-b-0 min-h-[120px] md:min-h-[140px] items-start">
                <div className="text-[17px] md:text-[19px] leading-tight font-medium text-[#173c38]/70 pt-0.5">06</div>
                <div>
                  <h3 className="m-0 mb-1.5 font-display text-[22px] md:text-[26px] leading-snug font-normal text-[#173c38]">
                    {lang === 'da' ? 'Selvkritik og håbløshed' : 'Self-Criticism & Hopelessness'}
                  </h3>
                  <p className="m-0 max-w-[390px] font-sans text-[14px] md:text-[15px] leading-relaxed text-[#2c443f]/90">
                    {lang === 'da' 
                      ? 'Negative tanker om dig selv og fremtiden kan fylde meget.' 
                      : 'Negative thought patterns about yourself and the future take up substantial space.'}
                  </p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ==========================================
          SEKTION 3 – DEPRESSION PÅVIRKER HELE MENNESKET (DIAMOND / CROSS DIAGRAM)
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40 overflow-hidden">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight leading-[1.15]">
              {cmsData?.diagram_overskrift ? getLocalizedValue(cmsData.diagram_overskrift) : (lang === 'da' ? 'Depression påvirker hele mennesket' : 'Depression Affects the Whole Person')}
            </h2>
            <span className="block w-[42px] h-[2px] bg-[#b89238] mx-auto mt-5 rounded-full" />
          </div>

          {/* Diagram Area - Desktop & Tablet layout */}
          <div className="hidden sm:block relative w-full max-w-[1000px] h-[520px] mx-auto my-6 select-none">
            
            {/* TANKER */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 text-center text-[#164d3c] z-10">
              <h3 className="font-display text-2xl font-normal tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'TANKER' : 'THOUGHTS'}
              </h3>
              <p className="font-sans text-sm sm:text-base text-[#264a40] leading-relaxed max-w-[280px]">
                {lang === 'da' ? (
                  <>Selvkritik, håbløshed og<br />negative fortolkninger.</>
                ) : (
                  <>Self-criticism, hopelessness, and<br />negative interpretations.</>
                )}
              </p>
            </div>

            {/* KROP */}
            <div className="absolute top-[215px] left-0 w-[230px] text-center text-[#164d3c] z-10">
              <h3 className="font-display text-2xl font-normal tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'KROP' : 'BODY'}
              </h3>
              <p className="font-sans text-sm sm:text-base text-[#264a40] leading-relaxed">
                {lang === 'da' ? (
                  <>Træthed samt ændringer<br />i søvn, appetit og<br />fysisk uro.</>
                ) : (
                  <>Fatigue and changes<br />in sleep, appetite, and<br />physical agitation.</>
                )}
              </p>
            </div>

            {/* FØLELSER */}
            <div className="absolute top-[215px] right-0 w-[230px] text-center text-[#164d3c] z-10">
              <h3 className="font-display text-2xl font-normal tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'FØLELSER' : 'EMOTIONS'}
              </h3>
              <p className="font-sans text-sm sm:text-base text-[#264a40] leading-relaxed">
                {lang === 'da' ? (
                  <>Tristhed, tomhed,<br />skyld eller irritabilitet.</>
                ) : (
                  <>Sadness, emptiness,<br />guilt, or irritability.</>
                )}
              </p>
            </div>

            {/* ADFÆRD */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[340px] text-center text-[#164d3c] z-10">
              <h3 className="font-display text-2xl font-normal tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'ADFÆRD' : 'BEHAVIOR'}
              </h3>
              <p className="font-sans text-sm sm:text-base text-[#264a40] leading-relaxed">
                {lang === 'da' ? (
                  <>Tilbagetrækning, mindre aktivitet<br />og færre positive erfaringer.</>
                ) : (
                  <>Withdrawal, reduced activity,<br />and fewer positive experiences.</>
                )}
              </p>
            </div>

            {/* SVG FORBINDELSESLINJER */}
            <svg
              className="absolute z-0 w-[650px] h-[370px] top-[75px] left-1/2 -translate-x-1/2 overflow-visible"
              viewBox="0 0 700 440"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <marker
                  id="arrow-depression"
                  markerWidth="8"
                  markerHeight="8"
                  refX="4"
                  refY="4"
                  orient="auto-start-reverse"
                  markerUnits="strokeWidth"
                >
                  <path d="M 0 0 L 8 4 L 0 8 z" fill="#164d3c" />
                </marker>
              </defs>

              {/* Lodret */}
              <line
                x1="350"
                y1="55"
                x2="350"
                y2="385"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />

              {/* Vandret */}
              <line
                x1="75"
                y1="220"
                x2="625"
                y2="220"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />

              {/* Top venstre */}
              <line
                x1="270"
                y1="65"
                x2="80"
                y2="185"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />

              {/* Top højre */}
              <line
                x1="430"
                y1="65"
                x2="620"
                y2="185"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />

              {/* Bund venstre */}
              <line
                x1="80"
                y1="255"
                x2="270"
                y2="375"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />

              {/* Bund højre */}
              <line
                x1="620"
                y1="255"
                x2="430"
                y2="375"
                stroke="#164d3c"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                markerStart="url(#arrow-depression)"
                markerEnd="url(#arrow-depression)"
              />
            </svg>
          </div>

          {/* Mobile Grid Layout (< sm) */}
          <div className="sm:hidden grid grid-cols-1 gap-4 my-6">
            <div className="bg-white/80 rounded-2xl p-6 border border-[#164d3c]/20 text-center text-[#164d3c] shadow-2xs">
              <h3 className="font-display text-xl font-medium tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'TANKER' : 'THOUGHTS'}
              </h3>
              <p className="font-sans text-sm text-[#264a40] leading-relaxed">
                {lang === 'da' ? 'Selvkritik, håbløshed og negative fortolkninger.' : 'Self-criticism, hopelessness, and negative interpretations.'}
              </p>
            </div>

            <div className="bg-white/80 rounded-2xl p-6 border border-[#164d3c]/20 text-center text-[#164d3c] shadow-2xs">
              <h3 className="font-display text-xl font-medium tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'KROP' : 'BODY'}
              </h3>
              <p className="font-sans text-sm text-[#264a40] leading-relaxed">
                {lang === 'da' ? 'Træthed samt ændringer i søvn, appetit og fysisk uro.' : 'Fatigue, along with changes in sleep, appetite, and physical restlessness.'}
              </p>
            </div>

            <div className="bg-white/80 rounded-2xl p-6 border border-[#164d3c]/20 text-center text-[#164d3c] shadow-2xs">
              <h3 className="font-display text-xl font-medium tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'FØLELSER' : 'EMOTIONS'}
              </h3>
              <p className="font-sans text-sm text-[#264a40] leading-relaxed">
                {lang === 'da' ? 'Tristhed, tomhed, skyld eller irritabilitet.' : 'Persistent sadness, emptiness, guilt, or irritability.'}
              </p>
            </div>

            <div className="bg-white/80 rounded-2xl p-6 border border-[#164d3c]/20 text-center text-[#164d3c] shadow-2xs">
              <h3 className="font-display text-xl font-medium tracking-wide mb-2 uppercase">
                {lang === 'da' ? 'ADFÆRD' : 'BEHAVIOR'}
              </h3>
              <p className="font-sans text-sm text-[#264a40] leading-relaxed">
                {lang === 'da' ? 'Tilbagetrækning, mindre aktivitet og færre positive erfaringer.' : 'Withdrawal, reduced activity, and fewer positive experiences.'}
              </p>
            </div>
          </div>

          {/* QUOTE BANNER */}
          <div className="mt-14 bg-[#1e3a35] rounded-3xl p-8 sm:p-12 border border-[#1e3a35] flex flex-col md:flex-row items-center justify-between gap-8 overflow-hidden shadow-xs">
            <div className="max-w-2xl">
              <div className="font-display text-2xl sm:text-3xl lg:text-4xl text-white font-medium leading-snug mb-4">
                {cmsData?.citat_banner_tekst 
                  ? getLocalizedValue(cmsData.citat_banner_tekst)
                  : (lang === 'da' ? 'Depression er ikke et tegn på svaghed – og den kan behandles.' : 'Depression is not a sign of weakness – and it can be treated.')}
              </div>
              <div className="w-12 h-[3px] bg-white/30 rounded-full" />
            </div>
            <div className="shrink-0 flex items-center justify-end">
              <img
                src={cmsData?.citat_banner_billede?.[0]
                  ? `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/${cmsData.citat_banner_billede[0].replace('https://assets.auxodev.dk/', '')}`
                  : "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-4-aug-2026-10-12-33.png"}
                alt="Terapeutisk illustration for håb og heling"
                className="max-h-[160px] sm:max-h-[200px] w-auto object-contain drop-shadow-md"
              />
            </div>
          </div>

          {/* DET KAN VÆRE FORSKELLIGT FRA PERSON TIL PERSON */}
          <div className="mt-20">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-normal text-petrol-dark">
                {cmsData?.forskelligt_overskrift
                  ? getLocalizedValue(cmsData.forskelligt_overskrift)
                  : (lang === 'da' ? 'Det kan være forskelligt fra person til person' : 'It Can Vary From Person to Person')}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              
              <div className="text-center p-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white border border-sand-medium/50 flex items-center justify-center text-petrol-dark shadow-2xs">
                  <Users className="w-7 h-7 stroke-[1.5]" />
                </div>
                <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                  {lang === 'da' ? 'Symptomerne varierer' : 'Symptoms Vary'}
                </h3>
                <p className="text-xs sm:text-sm text-text-dark/80 leading-relaxed">
                  {lang === 'da' ? 'Ikke alle oplever det på samme måde eller med samme intensitet.' : 'Not everyone experiences it the same way or with the same intensity.'}
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white border border-sand-medium/50 flex items-center justify-center text-petrol-dark shadow-2xs">
                  <Leaf className="w-7 h-7 stroke-[1.5]" />
                </div>
                <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                  {lang === 'da' ? 'Belastninger spiller ind' : 'Life Stressors Play In'}
                </h3>
                <p className="text-xs sm:text-sm text-text-dark/80 leading-relaxed">
                  {lang === 'da' ? 'Stress, tab, helbred og livssituation kan øge sårbarheden.' : 'Stress, loss, physical health, and life changes can increase vulnerability.'}
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white border border-sand-medium/50 flex items-center justify-center text-petrol-dark shadow-2xs">
                  <Heart className="w-7 h-7 stroke-[1.5]" />
                </div>
                <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                  {lang === 'da' ? 'Krop og psyke hænger sammen' : 'Mind & Body Interconnected'}
                </h3>
                <p className="text-xs sm:text-sm text-text-dark/80 leading-relaxed">
                  {lang === 'da' ? 'Fysiske og psykiske reaktioner kan påvirke hinanden i en ond cirkel.' : 'Physical and emotional reactions interact continuously in reciprocal cycles.'}
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white border border-sand-medium/50 flex items-center justify-center text-petrol-dark shadow-2xs">
                  <HandHeart className="w-7 h-7 stroke-[1.5]" />
                </div>
                <h3 className="font-display text-xl font-medium text-petrol-dark mb-2">
                  {lang === 'da' ? 'Hjælp kan gøre en forskel' : 'Help Makes a Difference'}
                </h3>
                <p className="text-xs sm:text-sm text-text-dark/80 leading-relaxed">
                  {lang === 'da' ? 'Behandling og støtte kan lette symptomerne og styrke vejen frem.' : 'Treatment and structured support can alleviate symptoms and strengthen resilience.'}
                </p>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ==========================================
          SEKTION 4 – HVAD SKER DER I HJERNE?
          ========================================== */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light border-b border-sand-medium/40" id="hjerne">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight mb-4">
              {cmsData?.hjerne_overskrift 
                ? getLocalizedValue(cmsData.hjerne_overskrift)
                : (lang === 'da' ? 'Hvad sker der i hjernen?' : 'What Happens in the Brain?')}
            </h2>
            <div className="w-12 h-[3px] bg-[#9c8a61] mx-auto mb-6 rounded-full" />
            {cmsData?.hjerne_beskrivelse ? (
              <div className="text-base sm:text-lg text-text-dark/85 leading-relaxed [&>p]:mb-3 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.hjerne_beskrivelse) }} />
            ) : (
              <p className="text-base sm:text-lg text-text-dark/85 leading-relaxed">
                {lang === 'da' 
                  ? 'Depression er forbundet med ændringer i hjernens funktion og samspillet mellem netværk, der blandt andet har betydning for belønning og motivation, opmærksomhed og følelsesregulering. Fundene varierer fra person til person, og der findes ikke én enkelt “depressionshjerne”.'
                  : 'Depression is associated with changes in neural networks and brain functioning that influence motivation, reward processing, concentration, and emotional regulation. Findings vary between individuals; there is no single monolithic “depression brain”.'}
              </p>
            )}
          </div>

          {/* INTERAKTIVT NEUROLOGISK SAMMENLIGNINGS-KORT */}
          <div className="bg-white border border-sand-medium/50 rounded-3xl p-6 sm:p-10 shadow-xs w-full">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              
              {/* Stand 1: Under en depression */}
              <div className="bg-sand-light p-6 rounded-2xl border border-sand-medium/30 text-center">
                <h3 className="font-display text-xl sm:text-2xl font-normal text-petrol-dark mb-4">
                  {cmsData?.hjerne_stand_1_titel 
                    ? getLocalizedValue(cmsData.hjerne_stand_1_titel)
                    : (lang === 'da' ? 'Under en depression' : 'During Depression')}
                </h3>
                <div className="relative w-full max-w-[280px] h-[200px] mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-full h-full" viewBox="0 0 420 320" fill="none">
                    <path d="M78 242c-25-18-38-51-26-82 9-22 26-31 29-58 4-40 39-64 79-53 22-23 65-24 88-2 35-8 72 13 76 48 30 17 39 57 19 83 12 36-15 68-49 69-20 30-60 31-85 8-38 14-75-5-86-36-15 0-31-2-45-11Z" stroke="#76aaa3" strokeWidth="2.5" />
                    <path d="M84 241c5 20 7 38 6 60" stroke="#76aaa3" strokeWidth="2" />
                    <g stroke="#a8c3bf" strokeWidth="1.5" strokeDasharray="3 3">
                      <path d="M107 111 170 96l42 49 43-33 40 64-56 28-69-4-72-43Z" />
                      <path d="M170 96v104m42-55 27 59m-132-93-9 46m157-45-16 92" />
                    </g>
                    <g fill="#f7f4ed" stroke="#76aaa3" strokeWidth="2">
                      <circle cx="107" cy="111" r="8" />
                      <circle cx="170" cy="96" r="8" />
                      <circle cx="212" cy="145" r="8" />
                      <circle cx="255" cy="112" r="8" />
                      <circle cx="295" cy="176" r="8" />
                      <circle cx="239" cy="204" r="8" />
                      <circle cx="170" cy="200" r="8" />
                      <circle cx="98" cy="157" r="8" />
                    </g>
                  </svg>
                </div>
                <p className="text-xs text-text-dark/70 leading-normal">
                  {cmsData?.hjerne_stand_1_tekst 
                    ? getLocalizedValue(cmsData.hjerne_stand_1_tekst)
                    : (lang === 'da' ? 'Dæmpet eller ubalanceret signalaktivitet mellem følelses- og opmærksomhedsnetværk.' : 'Attenuated or imbalanced signaling between emotional and attentional networks.')}
                </p>
              </div>

              {/* Stand 2: Ved gradvis bedring */}
              <div className="bg-sand-medium/60 p-6 rounded-2xl border border-petrol-dark/20 text-center">
                <h3 className="font-display text-xl sm:text-2xl font-normal text-petrol-dark mb-4">
                  {cmsData?.hjerne_stand_2_titel 
                    ? getLocalizedValue(cmsData.hjerne_stand_2_titel)
                    : (lang === 'da' ? 'Ved gradvis bedring' : 'During Gradual Recovery')}
                </h3>
                <div className="relative w-full max-w-[280px] h-[200px] mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-full h-full" viewBox="0 0 420 320" fill="none">
                    <path d="M78 242c-25-18-38-51-26-82 9-22 26-31 29-58 4-40 39-64 79-53 22-23 65-24 88-2 35-8 72 13 76 48 30 17 39 57 19 83 12 36-15 68-49 69-20 30-60 31-85 8-38 14-75-5-86-36-15 0-31-2-45-11Z" stroke="#0e4f43" strokeWidth="2.5" />
                    <path d="M84 241c5 20 7 38 6 60" stroke="#0e4f43" strokeWidth="2" />
                    <g stroke="#0e4f43" strokeWidth="2">
                      <path d="M107 111 170 96l42 49 43-33 40 64-56 28-69-4-72-43Z" />
                      <path d="M170 96v104m42-55 27 59m-132-93-9 46m157-45-16 92" />
                    </g>
                    <g stroke="#0e4f43" strokeWidth="2">
                      <circle cx="107" cy="111" r="8" fill="#9cb5ad" />
                      <circle cx="170" cy="96" r="8" fill="#527c72" />
                      <circle cx="212" cy="145" r="8" fill="#b8c7c2" />
                      <circle cx="255" cy="112" r="8" fill="#6f8e85" />
                      <circle cx="295" cy="176" r="8" fill="#7c998f" />
                      <circle cx="239" cy="204" r="8" fill="#ffffff" />
                      <circle cx="170" cy="200" r="8" fill="#0e4f43" />
                      <circle cx="98" cy="157" r="8" fill="#91aaa3" />
                    </g>
                  </svg>
                </div>
                <p className="text-xs text-petrol-dark/80 leading-normal">
                  {cmsData?.hjerne_stand_2_tekst 
                    ? getLocalizedValue(cmsData.hjerne_stand_2_tekst)
                    : (lang === 'da' ? 'Genvundet plasticitet og harmonisk samspil i hjernens netværk.' : 'Restored plasticity and balanced connectivity across brain networks.')}
                </p>
              </div>

            </div>

            <div className="mt-8 text-center pt-6 border-t border-sand-medium/30">
              <p className="font-display text-lg sm:text-xl text-petrol-dark font-normal max-w-2xl mx-auto mb-2">
                {cmsData?.hjerne_bund_tekst 
                  ? getLocalizedValue(cmsData.hjerne_bund_tekst)
                  : (lang === 'da' ? 'Ved effektiv behandling kan aktiviteten og samspillet i de hjernenetværk, der er påvirket under en depression, gradvist normaliseres.' : 'With effective treatment, neural activity and connectivity in affected brain networks can gradually normalize.')}
              </p>
              <p className="text-xs text-text-dark/70">
                {cmsData?.hjerne_bund_subtekst 
                  ? getLocalizedValue(cmsData.hjerne_bund_subtekst)
                  : (lang === 'da' ? 'Biologiske, psykologiske og sociale forhold virker sammen.' : 'Biological, psychological, and social factors interact.')}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ==========================================
          SEKTION 5 – HVORDAN BEHANDLER MAN DEPRESSION?
          ========================================== */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light border-b border-sand-medium/40" id="behandling">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.behandling_overskrift 
                ? getLocalizedValue(cmsData.behandling_overskrift)
                : (lang === 'da' ? 'Hvordan behandler man depression?' : 'How is Depression Treated?')}
            </h2>
            <div className="w-12 h-[3px] bg-[#9c8a61] mx-auto mt-4 mb-4 rounded-full" />
            <p className="text-base text-text-dark/80">
              {cmsData?.behandling_undertekst 
                ? getLocalizedValue(cmsData.behandling_undertekst)
                : (lang === 'da' ? 'Behandlingen tilpasses den enkelte og afhænger blandt andet af depressionens sværhedsgrad.' : 'Treatment is individually tailored and guided by the severity and nature of the depression.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Kort 1: Psykoterapi */}
            <div className="bg-white border border-sand-medium/50 rounded-3xl p-8 flex flex-col items-start shadow-xs hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-6">
                <MessageCircle className="w-7 h-7 stroke-[1.5]" />
              </div>
              <h3 className="font-display text-2xl font-normal text-petrol-dark mb-3">
                {cmsData?.behandling_psykoterapi_titel 
                  ? getLocalizedValue(cmsData.behandling_psykoterapi_titel)
                  : (lang === 'da' ? 'Psykoterapi' : 'Psychotherapy')}
              </h3>
              <p className="text-sm text-text-dark/80 leading-relaxed mb-4">
                {cmsData?.behandling_psykoterapi_tekst 
                  ? getLocalizedValue(cmsData.behandling_psykoterapi_tekst)
                  : (lang === 'da' ? 'Samtaleterapi kan hjælpe med at forstå mønstre og give plads til følelser, der har været svære at mærke, udtrykke eller give plads.' : 'Talking therapy helps identify unhelpful patterns and create space for feelings that have been challenging to acknowledge or express.')}
              </p>
            </div>

            {/* Kort 2: Medicin */}
            <div className="bg-white border border-sand-medium/50 rounded-3xl p-8 flex flex-col items-start shadow-xs hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-6">
                <Pill className="w-7 h-7 stroke-[1.5]" />
              </div>
              <h3 className="font-display text-2xl font-normal text-petrol-dark mb-3">
                {cmsData?.behandling_medicin_titel 
                  ? getLocalizedValue(cmsData.behandling_medicin_titel)
                  : (lang === 'da' ? 'Medicin' : 'Medication')}
              </h3>
              <p className="text-sm text-text-dark/80 leading-relaxed mb-4">
                {cmsData?.behandling_medicin_tekst 
                  ? getLocalizedValue(cmsData.behandling_medicin_tekst)
                  : (lang === 'da' ? 'Medicin kan lindre symptomer og give overskud til hverdagen. Det vurderes individuelt og følges tæt.' : 'Antidepressant medication can relieve core symptoms and restore capacity for daily life. Carefully evaluated and monitored.')}
              </p>
            </div>

            {/* Kort 3: Støtte i hverdagen */}
            <div className="bg-white border border-sand-medium/50 rounded-3xl p-8 flex flex-col items-start shadow-xs hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-6">
                <Users className="w-7 h-7 stroke-[1.5]" />
              </div>
              <h3 className="font-display text-2xl font-normal text-petrol-dark mb-3">
                {cmsData?.behandling_stoette_titel 
                  ? getLocalizedValue(cmsData.behandling_stoette_titel)
                  : (lang === 'da' ? 'Støtte i hverdagen' : 'Everyday Support')}
              </h3>
              <p className="text-sm text-text-dark/80 leading-relaxed mb-4">
                {cmsData?.behandling_stoette_tekst 
                  ? getLocalizedValue(cmsData.behandling_stoette_tekst)
                  : (lang === 'da' ? 'Relationer, struktur, søvn, bevægelse og meningsfulde aktiviteter kan styrke trivsel og modstandskraft.' : 'Supportive relationships, daily routine, sleep hygiene, gentle exercise, and meaningful activities bolster recovery.')}
              </p>
            </div>

          </div>

          <p className="text-center text-xs sm:text-sm text-text-dark/70 mt-8">
            {cmsData?.behandling_fodnote 
              ? getLocalizedValue(cmsData.behandling_fodnote)
              : (lang === 'da' ? 'Ved moderat eller svær depression kan psykoterapi og medicin kombineres.' : 'For moderate to severe depression, combining psychotherapy and medical treatment is often most effective.')}
          </p>

        </div>
      </section>

      {/* ==========================================
          SEKTION 6 – SMÅ SKRIDT KAN SKABE BEVÆGELSE (CTA)
          ========================================== */}
      {/* CTA Section */}
      <CtaSection 
        title={cmsData?.cta_overskrift 
          ? getLocalizedValue(cmsData.cta_overskrift) 
          : (lang === 'da' ? 'Små skridt kan skabe bevægelse' : 'Small Steps Can Create Movement')}
        desc={cmsData?.cta_beskrivelse 
          ? getLocalizedValue(cmsData.cta_beskrivelse)
          : (lang === 'da' 
            ? 'Man behøver ikke kunne se hele vejen frem. Behandling kan begynde med det, der er muligt lige nu – at forstå egne mønstre, skabe plads til følelser og gradvist genfinde ro og mening.'
            : 'You do not need to see the entire journey ahead. Treatment can begin with what is possible right now – understanding your patterns, making space for feelings, and gradually rediscovering calm and meaning.')}
        buttonText={cmsData?.cta_knap_tekst 
          ? getLocalizedValue(cmsData.cta_knap_tekst)
          : (lang === 'da' ? 'Kontakt os' : 'Contact us')}
        buttonLink={cmsData?.cta_knap_link || cmsData?.hero_cta_link || "/kontakt"}
      />

    </div>
  );
}
